import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Avatar,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CampaignIcon from '@mui/icons-material/Campaign';
import PersonIcon from '@mui/icons-material/Person';
import ScheduleIcon from '@mui/icons-material/Schedule';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import ReplyIcon from '@mui/icons-material/Reply';
import NotificationDetail from './NotificationDetail';

const NotificationCenter = ({ open, onClose, notifications, onMarkAsRead, onMarkAllAsRead, currentUser }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // 按类型筛选通知
  const getFilteredNotifications = () => {
    let filtered = notifications;
    
    // 🔧 只显示根消息（parent_id 为 null 或 undefined），回复消息不单独显示
    filtered = filtered.filter(n => !n.parent_id);
    
    // 按类型筛选
    if (activeTab === 0) return filtered; // 全部
    // 兼容两种字段名：notification_type（WebSocket推送）和 type（数据库查询）
    if (activeTab === 1) return filtered.filter(n => 
      (n.notification_type === 'broadcast' || n.type === 'broadcast')
    ); // 系统
    if (activeTab === 2) return filtered.filter(n => 
      (n.notification_type === 'personal' || n.type === 'personal')
    ); // 个人
    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();
  
  // 🔧 计算未读数量（只统计根消息，不包括回复）
  const rootNotifications = notifications.filter(n => !n.parent_id);
  const unreadCount = rootNotifications.filter(n => !n.is_read).length;
  const unreadBroadcastCount = rootNotifications.filter(n => 
    !n.is_read && (n.type === 'broadcast' || n.notification_type === 'broadcast')
  ).length;
  const unreadPersonalCount = rootNotifications.filter(n => 
    !n.is_read && (n.type === 'personal' || n.notification_type === 'personal')
  ).length;
  
  // 🔧 计算各类型数量（只统计根消息）
  const broadcastCount = rootNotifications.filter(n => 
    (n.notification_type === 'broadcast' || n.type === 'broadcast')
  ).length;
  const personalCount = rootNotifications.filter(n => 
    (n.notification_type === 'personal' || n.type === 'personal')
  ).length;

  // 格式化时间
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000 && now.getDate() === date.getDate()) {
      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="notification-dialog"
      PaperProps={{
        sx: {
          bgcolor: '#0f172a', // 纯色背景
          color: '#e2e8f0',
          borderRadius: '12px',
          border: '1px solid #334155',
          width: 'calc(100vw - 80px)',
          maxWidth: '960px',
          height: 'calc(100vh - 120px)',
          maxHeight: 'calc(100vh - 120px)',
          margin: '32px auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }
      }}
    >
      {/* 顶部装饰线 - 纯色 */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, left: 0, right: 0, height: '2px', 
        bgcolor: '#3b82f6', // 纯色
        zIndex: 2
      }} />

      {/* 标题栏 */}
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #334155',
        bgcolor: '#1e293b', // 纯色
        px: 3, py: 2.5,
        position: 'relative'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ 
            bgcolor: 'rgba(245, 158, 11, 0.1)', 
            p: 1, 
            borderRadius: '8px',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <NotificationsIcon sx={{ color: '#fbbf24', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: '0.5px', color: '#f1f5f9' }}>
              {t('notification_title')}
            </Typography>
            <Typography variant="caption" sx={{ 
              color: '#64748b', fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.7rem', letterSpacing: '1px', textTransform: 'uppercase'
            }}>
              {t('notification_subtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={onMarkAllAsRead}
            disabled={unreadCount === 0}
            sx={{
              color: '#3b82f6',
              borderColor: '#3b82f6',
              textTransform: 'none',
              '&:hover': {
                bgcolor: 'rgba(59, 130, 246, 0.1)',
                borderColor: '#2563eb'
              },
              '&.Mui-disabled': {
                color: '#475569',
                borderColor: '#475569'
              }
            }}
            variant="outlined"
          >
            {t('notification_mark_all_read')}
          </Button>
          <IconButton 
            onClick={onClose} 
            size="small" 
            sx={{ 
              color: '#94a3b8',
              '&:hover': { color: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.05)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Tab切换 */}
      <Box sx={{ 
        bgcolor: '#1e293b', 
        borderBottom: '1px solid #334155',
        px: 2
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: '3px' },
            '& .MuiTab-root': {
              color: '#94a3b8',
              textTransform: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              minHeight: '56px',
              '&.Mui-selected': { color: '#f1f5f9' },
              '&:hover': { color: '#cbd5e1' }
            }
          }}
        >
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('notification_tab_all')}
              {unreadCount > 0 && (
                <Chip 
                  label={unreadCount} 
                  size="small" 
                  sx={{ 
                    height: 20, fontSize: '0.75rem', fontWeight: 'bold',
                    bgcolor: '#ef4444', color: '#fff' 
                  }} 
                />
              )}
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('notification_tab_system')}
              {unreadBroadcastCount > 0 && (
                <Chip 
                  label={unreadBroadcastCount} 
                  size="small" 
                  sx={{ 
                    height: 20, fontSize: '0.75rem', fontWeight: 'bold',
                    bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5' 
                  }} 
                />
              )}
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {t('notification_tab_personal')}
              {unreadPersonalCount > 0 && (
                <Chip 
                  label={unreadPersonalCount} 
                  size="small" 
                  sx={{ 
                    height: 20, fontSize: '0.75rem', fontWeight: 'bold',
                    bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5' 
                  }} 
                />
              )}
            </Box>
          } />
        </Tabs>
      </Box>

      {/* 通知列表 */}
      <DialogContent sx={{ 
        padding: 0, 
        flex: 1,
        bgcolor: '#0f172a',
        overflowY: 'auto'
      }}>
        {filteredNotifications.length === 0 ? (
          <Box sx={{ 
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            height: '100%', color: '#64748b', gap: 2
          }}>
            <NotificationsIcon sx={{ fontSize: 64, opacity: 0.2 }} />
            <Typography variant="body1">{t('notification_empty')}</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {filteredNotifications.map((notification, index) => (
              <Box
                key={notification.id}
                onClick={() => {
                  setSelectedNotification(notification);
                  setDetailOpen(true);
                  if (!notification.is_read) {
                    onMarkAsRead(notification.id);
                  }
                }}
                sx={{
                  p: 2.5,
                  borderBottom: '1px solid #334155',
                  cursor: 'pointer',
                  bgcolor: notification.is_read ? 'transparent' : 'rgba(59, 130, 246, 0.05)',
                  position: 'relative',
                  transition: 'background-color 0.2s', // 仅过渡背景色
                  '&:hover': {
                    bgcolor: '#1e293b',
                    pl: 3
                  },
                  display: 'flex',
                  gap: 2
                }}
              >
                {/* 左侧未读指示条 - 移除阴影 */}
                {!notification.is_read && (
                  <Box sx={{
                    position: 'absolute', left: 0, top: 0, bottom: 0, width: '3px',
                    bgcolor: notification.level === 'urgent' ? '#ef4444' : '#3b82f6'
                  }} />
                )}

                {/* 图标 */}
                <Avatar sx={{ 
                  bgcolor: notification.is_read ? '#334155' : 'rgba(59, 130, 246, 0.15)',
                  color: notification.is_read ? '#94a3b8' : '#3b82f6',
                  width: 40, height: 40
                }}>
                  {(notification.type === 'broadcast' || notification.notification_type === 'broadcast') ? (
                    <CampaignIcon fontSize="small" />
                  ) : (
                    <PersonIcon fontSize="small" />
                  )}
                </Avatar>

                {/* 内容区域 */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ 
                        color: notification.is_read ? '#94a3b8' : '#f1f5f9',
                        fontWeight: notification.is_read ? 400 : 600,
                        fontSize: '0.95rem'
                      }}>
                        {notification.sender_name || t('notification_system_fallback')}
                      </Typography>
                      {notification.level === 'urgent' && (
                        <Chip 
                        label={t('notification_urgent')}
                          size="small" 
                          sx={{ 
                            height: 18, fontSize: '0.65rem', fontWeight: 'bold',
                            bgcolor: 'rgba(239, 68, 68, 0.2)', color: '#fca5a5',
                            border: '1px solid rgba(239, 68, 68, 0.3)'
                          }} 
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b' }}>
                      <ScheduleIcon sx={{ fontSize: 12 }} />
                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                        {formatTime(notification.created_at)}
                      </Typography>
                    </Box>
                  </Box>

                  <Typography variant="body1" sx={{ 
                    color: notification.is_read ? '#cbd5e1' : '#fff',
                    mb: 0.5,
                    fontWeight: notification.is_read ? 400 : 500,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {notification.title}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    color: '#94a3b8', 
                    fontSize: '0.85rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    lineHeight: 1.5
                  }}>
                    {notification.content}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {notification.reply_count > 0 && (
                        <Chip 
                          icon={<ReplyIcon sx={{ fontSize: '14px !important' }} />} 
                          label={t('notification_reply_count', { count: notification.reply_count })}
                          size="small" 
                          variant="outlined"
                          sx={{ 
                            height: 22, fontSize: '0.75rem', 
                            borderColor: 'rgba(59, 130, 246, 0.2)', 
                            color: '#60a5fa',
                            '& .MuiChip-icon': { color: '#60a5fa' }
                          }} 
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, opacity: 0, transition: 'opacity 0.2s', '.MuiBox-root:hover &': { opacity: 1 } }}>
                       {!notification.is_read && (
                        <Tooltip title={t('notification_mark_read')}>
                          <IconButton 
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onMarkAsRead(notification.id);
                            }}
                            sx={{ color: '#94a3b8', '&:hover': { color: '#3b82f6' } }}
                          >
                            <MarkChatReadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Button 
                        size="small" 
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNotification(notification);
                          setDetailOpen(true);
                          if (!notification.is_read) {
                            onMarkAsRead(notification.id);
                          }
                        }}
                        sx={{ 
                          minWidth: '60px', height: 24, fontSize: '0.75rem',
                          borderColor: '#475569', color: '#e2e8f0',
                          '&:hover': { borderColor: '#f1f5f9', bgcolor: 'rgba(255,255,255,0.05)' }
                        }}
                      >
                        {t('notification_view_detail')}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* 通知详情对话框 */}
      <NotificationDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        notification={selectedNotification}
        currentUser={currentUser}
        onReplySubmit={() => {
          console.log('回复已提交');
        }}
      />
    </Dialog>
  );
};

export default NotificationCenter;
